// popup ของปุ่ม AI Suggest — สองจังหวะ: ได้รายชื่อคู่แข่งก่อน แล้วค่อยหาช่องทาง
//
// เปิดปุ๊บ Claude หา direct competitor ของ Product Group นี้มาให้เป็น capsule ก่อน
// (รอบนี้ค้นน้อย จึงถูกและเร็ว) ผู้ใช้กด × ตัดรายชื่อที่ไม่เกี่ยวออก แล้วค่อยกด "ค้นหา"
// ให้ไปหาช่องทางของเฉพาะที่เหลือ — จะได้ไม่จ่ายค่าค้นช่องทางให้แบรนด์ที่ยังไงก็ไม่เอา
//
// ผลลัพธ์ยังไม่ถูกเพิ่มเข้ารายการจนกว่าจะกด + ต่อรายการ · เปิดค้างไว้เพิ่มได้หลายแบรนด์
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiErrorMessage } from "@shared/api/client";
import { IconClose, IconSearch } from "@shared/ui/icons";
import {
  lookupBrandChannels,
  type SuggestedCompetitor,
  suggestCompetitors,
} from "../api/brand-assets-api";

export function CompetitorFinder({
  brandId,
  brandName,
  added,
  onAdd,
  onClose,
}: {
  brandId: string;
  /** ชื่อ Product Group ที่เปิดอยู่ — ตัวตั้งของการหาคู่แข่ง */
  brandName: string;
  /** ชื่อที่อยู่ในรายการแล้ว (ตัวพิมพ์เล็ก) — กันเพิ่มซ้ำ */
  added: Set<string>;
  onAdd: (c: SuggestedCompetitor) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<{ name: string; note: string }[]>([]);
  const [summary, setSummary] = useState("");
  const [results, setResults] = useState<SuggestedCompetitor[]>([]);
  const [msg, setMsg] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Esc ปิด · ล็อกการเลื่อนหน้าหลัง · โฟกัสช่องพิมพ์ทันที (แบบเดียวกับ PreviewModal)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  /** ชื่อที่ยังไม่อยู่ในรายชื่อที่เลือกไว้ (เทียบแบบไม่สนตัวพิมพ์) */
  const add = (list: { name: string; note: string }[]) =>
    setPicked((cur) => {
      const seen = new Set(cur.map((x) => x.name.toLowerCase()));
      const fresh = list.filter((x) => x.name && !seen.has(x.name.toLowerCase()));
      return [...cur, ...fresh];
    });

  // เป็น query ไม่ใช่ mutation เพราะต้องเริ่มเองทันทีที่เปิด — ปุ่ม AI Suggest ถูกกดมา
  // เพื่อการนี้อยู่แล้ว ไม่ต้องให้กดซ้ำอีกที · ไม่เก็บแคช เปิดใหม่ = ค้นใหม่
  const rivals = useQuery({
    queryKey: ["rivals", brandId],
    queryFn: () => suggestCompetitors(brandId),
    gcTime: 0,
    retry: false,
  });

  const lookup = useMutation({
    mutationFn: () =>
      lookupBrandChannels(
        brandId,
        picked.map((p) => p.name),
      ),
    onSuccess: (r) => {
      setSummary(r.summary);
      setResults(r.competitors);
      setMsg(r.competitors.length ? "" : "ไม่พบช่องทางของแบรนด์ที่เลือกไว้");
    },
    onError: (e) => setMsg(apiErrorMessage(e)),
  });

  // เอาผลรอบแรกลงเป็น capsule ครั้งเดียว — ปรับ state ตอน render ตามแนวที่ React แนะนำ
  // สำหรับ "state ที่ขึ้นกับ props/ข้อมูลที่เพิ่งมาถึง" (ไม่ใช่ effect)
  const [seeded, setSeeded] = useState(false);
  if (!seeded && rivals.data) {
    setSeeded(true);
    setSummary(rivals.data.summary);
    add(rivals.data.competitors.map((c) => ({ name: c.name, note: c.note })));
    if (!rivals.data.competitors.length) {
      setMsg("Claude ยังหาคู่แข่งของแบรนด์นี้ไม่เจอ — พิมพ์ชื่อเองได้ด้านบน");
    }
  }
  if (!seeded && rivals.isError) {
    setSeeded(true);
    setMsg(apiErrorMessage(rivals.error));
  }

  // ล็อกเฉพาะตอนค้นช่องทาง — ระหว่างที่ AI ยังหารายชื่ออยู่ ผู้ใช้พิมพ์ชื่อเองไปพลางได้
  // (ผลที่ตามมาทีหลังจะถูกรวมเข้าโดยไม่ทับของที่พิมพ์เอง)
  const busy = lookup.isPending;
  const typed = name.trim();

  return (
    <div className="doc-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="ai-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="ค้นหาคู่แข่งด้วย AI"
      >
        <div className="doc-modal-head">
          <div className="doc-modal-title">
            <strong>✨ ค้นหาคู่แข่งด้วย AI</strong>
            <div className="muted">
              คู่แข่งของ <b>{brandName}</b> — กด × ตัดรายชื่อที่ไม่เกี่ยวออก
              แล้วกดค้นหาเพื่อไปหาช่องทาง
            </div>
          </div>
          <button type="button" className="icon-btn" title="ปิด" onClick={onClose}>
            <IconClose />
          </button>
        </div>

        <div className="ai-modal-body">
          <form
            className="ai-find"
            onSubmit={(e) => {
              e.preventDefault();
              if (!typed || busy) return;
              setMsg("");
              add([{ name: typed, note: "เพิ่มเอง" }]);
              setName("");
            }}
          >
            <div className="search-box grow-box">
              <IconSearch />
              <input
                ref={inputRef}
                type="text"
                placeholder="เพิ่มชื่อแบรนด์เอง เช่น Dove, Protex"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </div>
            <button type="submit" className="btn-outline" disabled={!typed || busy}>
              ＋ เพิ่มชื่อ
            </button>
          </form>

          {rivals.isPending && <p className="muted">Claude กำลังหาคู่แข่งของ {brandName}…</p>}

          {!!picked.length && (
            <>
              <div className="ai-picks">
                {picked.map((p) => (
                  <span className="ai-pick" key={p.name} title={p.note}>
                    {p.name}
                    <button
                      type="button"
                      className="ai-pick-x"
                      title={`ตัด ${p.name} ออก`}
                      disabled={busy}
                      onClick={() => setPicked((cur) => cur.filter((x) => x.name !== p.name))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="ai-find">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy}
                  onClick={() => {
                    setMsg("");
                    lookup.mutate();
                  }}
                >
                  {lookup.isPending ? "กำลังค้น…" : `ค้นหาช่องทางของ ${picked.length} แบรนด์`}
                </button>
              </div>
            </>
          )}

          {lookup.isPending && <p className="muted">Claude กำลังค้นเว็บ — อาจใช้เวลาสักครู่…</p>}
          {msg && <p className="muted small-note">{msg}</p>}
          {summary && <p className="muted small-note asset-summary">{summary}</p>}

          {!!results.length && (
            <div className="ai-results">
              {results.map((c) => {
                const already = added.has(c.name.trim().toLowerCase());
                return (
                  <div className="ai-result" key={c.name}>
                    <div className="ai-result-head">
                      <span className="grow">
                        <span className="mem-name">{c.name}</span>
                        {c.note && <span className="mem-meta">{c.note}</span>}
                      </span>
                      <button
                        type="button"
                        className="btn-primary ai-add"
                        disabled={already}
                        title={already ? "มีในรายการแล้ว" : "เพิ่มเข้ารายการคู่แข่ง"}
                        onClick={() => onAdd(c)}
                      >
                        {already ? "เพิ่มแล้ว" : "＋ เพิ่ม"}
                      </button>
                    </div>
                    {/* ช่องทางที่หาได้ — กดไปดูของจริงได้ก่อนตัดสินใจเพิ่ม */}
                    <div className="asset-chips">
                      {c.channels.length ? (
                        c.channels.map((ch) => (
                          <span className="asset-chip-wrap on" key={ch.platform}>
                            <span className="asset-chip">{ch.platform}</span>
                            {ch.url && (
                              <a
                                className="asset-chip-go"
                                href={ch.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                title={`เปิด ${ch.platform} ของ ${c.name}`}
                              >
                                ↗
                              </a>
                            )}
                          </span>
                        ))
                      ) : (
                        <span className="muted small-note">ไม่พบช่องทางของแบรนด์นี้</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
